import React, { useEffect, useMemo, useRef, useState } from "react";

// Every image in src/assets/images/parallax is picked up automatically - adding
// or removing a file there needs no code change. require.context is resolved by
// webpack at build time, so the folder must exist in the repo (build_portal.sh
// builds from a fresh GitHub clone, not this working directory).
const context = require.context(
  "../../assets/images/parallax",
  false,
  /\.(jpe?g|png|webp|avif)$/i
);

// background-attachment: fixed sizes the background against the VIEWPORT, not
// this element, so on a 1920x1080 screen a 1600x2560 portrait scales to 1920
// wide and only ~35% of its height is ever on screen. Percentage positioning
// aligns the image's point at P% with the viewport's point at P%, so setting P
// to where a face sits in the artwork puts that face at P% down the screen.
// Values below are measured per image; see the session notes in git history.
const FOCUS = {
  "HomeBg.jpg": "center 48%", // letterboxed landscape art; cast sits 44-52%
  "parralaxnhs3.jpg": "center 20%", // two faces near the top edge
  "parralaxnhsichiasrialjeremy.jpg": "center 16%", // keeps Asrial + Ichi; Jeremy (~62%) falls outside
  "parralaxplanet.jpg": "center 33%",
  "parralaxshanna.jpg": "center 42%",
};

// Comic covers put faces in the upper half far more often than dead centre, so
// a new file dropped in the folder starts here until it is measured.
const DEFAULT_POSITION = "center 40%";

// CRA emits images as asset modules; depending on esModule interop the context
// returns either the URL string or a module wrapping it.
const IMAGES = context.keys().map((key) => {
  const mod = context(key);
  const src = mod && mod.default ? mod.default : mod;
  const name = key.replace(/^\.\//, "");
  return { src, position: FOCUS[name] || DEFAULT_POSITION };
});

const INTERVAL_MS = 8000;
const FADE_MS = 1200;

// Fisher-Yates. Shuffled once per mount, so a full pass shows every image before
// any repeats, and each visit gets a different order.
function shuffle(items) {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function ParallaxBackground({ isMobile }) {
  const order = useMemo(() => shuffle(IMAGES), []);
  const [index, setIndex] = useState(0);

  // Layers only get a backgroundImage once they have been reached, so the
  // remaining images are not all fetched on first paint - these files run to
  // several MB each. Once reached, a layer keeps its image so cycling back to
  // it is just an opacity change.
  const [maxReached, setMaxReached] = useState(0);

  // Auto-cycling is motion the user did not ask for; hold on one image when the
  // OS asks for reduced motion.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const preloaderRef = useRef(null);

  useEffect(() => {
    if (reducedMotion || order.length < 2) return undefined;

    const timer = setTimeout(() => {
      const next = (index + 1) % order.length;

      // Decode the next image before swapping opacity, otherwise the fade
      // reveals a layer that is still downloading and reads as a flash.
      const img = new window.Image();
      preloaderRef.current = img;

      const advance = () => {
        setMaxReached((m) => Math.max(m, next));
        setIndex(next);
      };

      img.onload = advance;
      img.onerror = advance; // a missing file should not stall the rotation
      img.src = order[next].src;
    }, INTERVAL_MS);

    return () => {
      clearTimeout(timer);
      if (preloaderRef.current) {
        preloaderRef.current.onload = null;
        preloaderRef.current.onerror = null;
      }
    };
  }, [index, order, reducedMotion]);

  // An empty folder would otherwise render nothing at all and leave the hero's
  // dark overlay sitting on the page background.
  if (order.length === 0) {
    return (
      <div
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, backgroundColor: "#0d1b2a", zIndex: 0 }}
      />
    );
  }

  return (
    <>
      {order.map((item, i) => (
        <div
          key={item.src}
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "#0d1b2a",
            backgroundImage: i <= maxReached ? `url(${item.src})` : "none",
            backgroundAttachment: isMobile ? "scroll" : "fixed",
            backgroundSize: "cover",
            backgroundPosition: item.position,
            backgroundRepeat: "no-repeat",
            opacity: i === index ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease-in-out`,
            zIndex: 0,
          }}
        />
      ))}
    </>
  );
}
